import type {
  ActivityMetadata,
  ActivitySessionConfig,
} from "@/db/schema/activities";

export type ActivitySeed = {
  id: string;
  name: string;
  metric: string;
  iconKey: string;
  order: number;
  metadata: ActivityMetadata;
};

// Individual events that do not support scoring (used for non-trackable + mini-app activities)
const groupSessionConfig = (max: number = 50): ActivitySessionConfig => ({
  formats: ["group"],
  defaultFormat: "group",
  supportsCompetition: false,
  participantRange: { group: { min: 1, max } },
});

// Racquet sports with singles/doubles
const racquetConfig = (
  defaultFormat: "singles" | "doubles" = "singles",
): ActivitySessionConfig => ({
  formats: ["singles", "doubles"],
  defaultFormat,
  supportsCompetition: true,
  participantRange: {
    singles: { min: 2, max: 2 },
    doubles: { min: 4, max: 4 },
  },
});

// Singles only (1v1)
const singlesOnlyConfig: ActivitySessionConfig = {
  formats: ["singles"],
  defaultFormat: "singles",
  supportsCompetition: true,
  participantRange: { singles: { min: 2, max: 2 } },
};

// Doubles only (padel)
const doublesOnlyConfig: ActivitySessionConfig = {
  formats: ["doubles"],
  defaultFormat: "doubles",
  supportsCompetition: true,
  participantRange: { doubles: { min: 4, max: 4 } },
};

// Team sports
const teamConfig = (min: number, max: number): ActivitySessionConfig => ({
  formats: ["team"],
  defaultFormat: "team",
  supportsCompetition: true,
  participantRange: { team: { min, max } },
  resultSchema: "team_score",
});

const activitySeedData = [
  {
    id: "running",
    name: "Running",
    metric: "Pace (5K min/km)",
    iconKey: "running-man",
    order: 1,
    metadata: {
      levels: [
        { score: 1, label: "Beginner", sublabel: ">8:00/km" },
        { score: 2, label: "Novice", sublabel: "7:00-8:00/km" },
        { score: 3, label: "Intermediate", sublabel: "6:00-7:00/km" },
        { score: 4, label: "Advanced", sublabel: "5:30-6:00/km" },
        { score: 5, label: "Expert", sublabel: "5:00-5:30/km" },
        { score: 6, label: "Elite", sublabel: "4:30-5:00/km" },
        { score: 7, label: "Pro", sublabel: "<4:30/km" },
      ],
      sessionConfig: groupSessionConfig(200),
    },
  },
  {
    id: "tennis",
    name: "Tennis",
    metric: "Skill Level",
    iconKey: "tennis-ball",
    order: 2,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "Learning basics" },
        { score: 2, label: "Beginner", sublabel: "Can rally slowly" },
        { score: 3, label: "Beginner+", sublabel: "Consistent rallies" },
        { score: 4, label: "Intermediate", sublabel: "Good technique" },
        { score: 5, label: "Intermediate+", sublabel: "Tactical play" },
        { score: 6, label: "Advanced", sublabel: "Tournament level" },
        { score: 7, label: "Pro", sublabel: "Professional" },
      ],
      sessionConfig: racquetConfig("singles"),
    },
  },
  {
    id: "football",
    name: "Football",
    metric: "Experience Level",
    iconKey: "football",
    order: 3,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "Learning basics" },
        { score: 2, label: "Beginner", sublabel: "Casual kickabouts" },
        { score: 3, label: "Beginner+", sublabel: "Basic positioning" },
        { score: 4, label: "Intermediate", sublabel: "Local turf leagues" },
        { score: 5, label: "Intermediate+", sublabel: "Club level" },
        { score: 6, label: "Advanced", sublabel: "Competitive leagues" },
        { score: 7, label: "Pro", sublabel: "Semi-pro / Pro" },
      ],
      sessionConfig: teamConfig(4, 30),
    },
  },
  {
    id: "badminton",
    name: "Badminton",
    metric: "Skill Level",
    iconKey: "shuttlecock",
    order: 4,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "Learning basics" },
        { score: 2, label: "Beginner", sublabel: "Short rallies" },
        { score: 3, label: "Beginner+", sublabel: "Consistent rallies" },
        { score: 4, label: "Intermediate", sublabel: "Good technique" },
        { score: 5, label: "Intermediate+", sublabel: "Tactical play" },
        { score: 6, label: "Advanced", sublabel: "Tournament level" },
        { score: 7, label: "Pro", sublabel: "Professional" },
      ],
      sessionConfig: racquetConfig("singles"),
    },
  },
  {
    id: "table-tennis",
    name: "Table Tennis",
    metric: "Skill Level",
    iconKey: "table-tennis",
    order: 5,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "Learning basics" },
        { score: 2, label: "Beginner", sublabel: "Basic serves" },
        { score: 3, label: "Beginner+", sublabel: "Consistent rallies" },
        { score: 4, label: "Intermediate", sublabel: "Good spin" },
        { score: 5, label: "Intermediate+", sublabel: "Tactical play" },
        { score: 6, label: "Advanced", sublabel: "Tournament level" },
        { score: 7, label: "Pro", sublabel: "Professional" },
      ],
      sessionConfig: racquetConfig("singles"),
    },
  },
  {
    id: "yoga",
    name: "Yoga",
    metric: "Practice Level",
    iconKey: "meditation",
    order: 6,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "First classes" },
        { score: 2, label: "Beginner", sublabel: "Learning poses" },
        { score: 3, label: "Beginner+", sublabel: "Regular practice" },
        { score: 4, label: "Intermediate", sublabel: "Flowing sequences" },
        { score: 5, label: "Intermediate+", sublabel: "Advanced poses" },
        { score: 6, label: "Advanced", sublabel: "Inversions & flows" },
        { score: 7, label: "Teacher", sublabel: "Certified instructor" },
      ],
      sessionConfig: groupSessionConfig(50),
    },
  },
  {
    id: "trekking",
    name: "Trekking",
    metric: "Distance / Elevation",
    iconKey: "trekking",
    order: 7,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "Easy day hikes" },
        { score: 2, label: "Beginner", sublabel: "Moderate day treks" },
        { score: 3, label: "Beginner+", sublabel: "Multi-day trekking" },
        { score: 4, label: "Intermediate", sublabel: "Alpine trails" },
        { score: 5, label: "Intermediate+", sublabel: "High-altitude passes" },
        { score: 6, label: "Advanced", sublabel: "Technical terrain" },
        { score: 7, label: "Expedition", sublabel: "High peaks / Wilderness" },
      ],
      sessionConfig: groupSessionConfig(50),
    },
  },
  {
    id: "pilates",
    name: "Pilates",
    metric: "Experience Level",
    iconKey: "pilates",
    order: 8,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "First classes" },
        { score: 2, label: "Beginner", sublabel: "Learning control" },
        { score: 3, label: "Beginner+", sublabel: "Regular practice" },
        { score: 4, label: "Intermediate", sublabel: "Mat mastery" },
        { score: 5, label: "Intermediate+", sublabel: "Reformer work" },
        { score: 6, label: "Advanced", sublabel: "Advanced apparatus" },
        { score: 7, label: "Instructor", sublabel: "Certified teacher" },
      ],
      sessionConfig: groupSessionConfig(30),
    },
  },
  {
    id: "padel",
    name: "Padel",
    metric: "Skill Level",
    iconKey: "paddle",
    order: 9,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "Learning basics" },
        { score: 2, label: "Beginner", sublabel: "Basic rallies" },
        { score: 3, label: "Beginner+", sublabel: "Wall rebounds" },
        { score: 4, label: "Intermediate", sublabel: "Good technique" },
        { score: 5, label: "Intermediate+", sublabel: "Tactical play" },
        { score: 6, label: "Advanced", sublabel: "Tournament level" },
        { score: 7, label: "Pro", sublabel: "Professional" },
      ],
      sessionConfig: doublesOnlyConfig,
    },
  },
  {
    id: "pickleball",
    name: "Pickleball",
    metric: "Skill Level",
    iconKey: "pickleball",
    order: 10,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "1.0-1.5" },
        { score: 2, label: "Beginner", sublabel: "2.0-2.5" },
        { score: 3, label: "Beginner+", sublabel: "3.0" },
        { score: 4, label: "Intermediate", sublabel: "3.5" },
        { score: 5, label: "Intermediate+", sublabel: "4.0" },
        { score: 6, label: "Advanced", sublabel: "4.5-5.0" },
        { score: 7, label: "Pro", sublabel: "5.0+" },
      ],
      sessionConfig: racquetConfig("doubles"),
    },
  },
  {
    id: "squash",
    name: "Squash",
    metric: "Skill Level",
    iconKey: "squash",
    order: 11,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "Learning basics" },
        { score: 2, label: "Beginner", sublabel: "Basic rallies" },
        { score: 3, label: "Beginner+", sublabel: "Consistent play" },
        { score: 4, label: "Intermediate", sublabel: "Good technique" },
        { score: 5, label: "Intermediate+", sublabel: "Tactical play" },
        { score: 6, label: "Advanced", sublabel: "Tournament level" },
        { score: 7, label: "Pro", sublabel: "Professional" },
      ],
      sessionConfig: singlesOnlyConfig,
    },
  },
  {
    id: "snooker",
    name: "Snooker",
    metric: "Skill Level",
    iconKey: "snooker",
    order: 12,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "Learning basics" },
        { score: 2, label: "Beginner", sublabel: "Occasional play" },
        { score: 3, label: "Beginner+", sublabel: "Consistent shots" },
        { score: 4, label: "Intermediate", sublabel: "Breaks 10+" },
        { score: 5, label: "Intermediate+", sublabel: "Breaks 20+" },
        { score: 6, label: "Advanced", sublabel: "Breaks 40+" },
        { score: 7, label: "Pro", sublabel: "Breaks 80+" },
      ],
      sessionConfig: racquetConfig("singles"),
    },
  },
  {
    id: "recovery",
    name: "Recovery",
    metric: "Session Type",
    iconKey: "recovery",
    order: 13,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "First sessions" },
        { score: 2, label: "Beginner", sublabel: "5-10 min" },
        { score: 3, label: "Beginner+", sublabel: "10-15 min" },
        { score: 4, label: "Intermediate", sublabel: "15-20 min" },
        { score: 5, label: "Intermediate+", sublabel: "20-30 min" },
        { score: 6, label: "Advanced", sublabel: "30+ min" },
        { score: 7, label: "Instructor", sublabel: "Guides sessions" },
      ],
      sessionConfig: groupSessionConfig(20),
    },
  },
  {
    id: "hyrox",
    name: "Hyrox",
    metric: "Finish Time",
    iconKey: "hyrox",
    order: 14,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: ">100 mins" },
        { score: 2, label: "Beginner", sublabel: "90-100 mins" },
        { score: 3, label: "Beginner+", sublabel: "80-90 mins" },
        { score: 4, label: "Intermediate", sublabel: "70-80 mins" },
        { score: 5, label: "Intermediate+", sublabel: "65-70 mins" },
        { score: 6, label: "Advanced", sublabel: "60-65 mins" },
        { score: 7, label: "Pro", sublabel: "<60 mins" },
      ],
      sessionConfig: groupSessionConfig(100),
    },
  },
  {
    id: "swimming",
    name: "Swimming",
    metric: "100m Time",
    iconKey: "swimming",
    order: 15,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "Learning strokes" },
        { score: 2, label: "Beginner", sublabel: ">2:30/100m" },
        { score: 3, label: "Beginner+", sublabel: "2:00-2:30/100m" },
        { score: 4, label: "Intermediate", sublabel: "1:40-2:00/100m" },
        { score: 5, label: "Intermediate+", sublabel: "1:25-1:40/100m" },
        { score: 6, label: "Advanced", sublabel: "1:10-1:25/100m" },
        { score: 7, label: "Pro", sublabel: "<1:10/100m" },
      ],
      sessionConfig: groupSessionConfig(50),
    },
  },
  {
    id: "weight-training",
    name: "Weight Training",
    metric: "Experience Level",
    iconKey: "weight-lifting",
    order: 16,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "First workouts" },
        { score: 2, label: "Beginner", sublabel: "<6 months" },
        { score: 3, label: "Beginner+", sublabel: "6-12 months" },
        { score: 4, label: "Intermediate", sublabel: "1-2 years" },
        { score: 5, label: "Intermediate+", sublabel: "2-4 years" },
        { score: 6, label: "Advanced", sublabel: "4+ years" },
        { score: 7, label: "Pro", sublabel: "Competition level" },
      ],
      sessionConfig: groupSessionConfig(20),
    },
  },
  {
    id: "day-retreats",
    name: "Day Retreats",
    metric: "Participation",
    iconKey: "day-retreats",
    order: 17,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "First retreat" },
        { score: 2, label: "Beginner", sublabel: "1-2 retreats" },
        { score: 3, label: "Beginner+", sublabel: "3-5 retreats" },
        { score: 4, label: "Intermediate", sublabel: "5-10 retreats" },
        { score: 5, label: "Intermediate+", sublabel: "Regular attendee" },
        { score: 6, label: "Advanced", sublabel: "Frequent participant" },
        { score: 7, label: "Facilitator", sublabel: "Hosts sessions" },
      ],
      sessionConfig: groupSessionConfig(100),
    },
  },
  {
    id: "basketball",
    name: "Basketball",
    metric: "Skill Level",
    iconKey: "basketball",
    order: 18,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "Learning basics" },
        { score: 2, label: "Beginner", sublabel: "Casual pickup" },
        { score: 3, label: "Beginner+", sublabel: "Basic plays" },
        { score: 4, label: "Intermediate", sublabel: "Local leagues" },
        { score: 5, label: "Intermediate+", sublabel: "Club level" },
        { score: 6, label: "Advanced", sublabel: "Competitive" },
        { score: 7, label: "Pro", sublabel: "Semi-pro / Pro" },
      ],
      sessionConfig: teamConfig(4, 14),
    },
  },
  {
    id: "volleyball",
    name: "Volleyball",
    metric: "Skill Level",
    iconKey: "volleyball",
    order: 19,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "Learning basics" },
        { score: 2, label: "Beginner", sublabel: "Basic serves" },
        { score: 3, label: "Beginner+", sublabel: "Consistent play" },
        { score: 4, label: "Intermediate", sublabel: "Team tactics" },
        { score: 5, label: "Intermediate+", sublabel: "League play" },
        { score: 6, label: "Advanced", sublabel: "Tournaments" },
        { score: 7, label: "Pro", sublabel: "National level" },
      ],
      sessionConfig: teamConfig(6, 16),
    },
  },
  {
    id: "throwball",
    name: "Throwball",
    metric: "Skill Level",
    iconKey: "throwball",
    order: 20,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "Learning basics" },
        { score: 2, label: "Beginner", sublabel: "Basic throws" },
        { score: 3, label: "Beginner+", sublabel: "Consistent play" },
        { score: 4, label: "Intermediate", sublabel: "Team tactics" },
        { score: 5, label: "Intermediate+", sublabel: "League play" },
        { score: 6, label: "Advanced", sublabel: "Tournaments" },
        { score: 7, label: "Pro", sublabel: "State / National" },
      ],
      sessionConfig: teamConfig(6, 16),
    },
  },
  {
    id: "climbing",
    name: "Climbing",
    metric: "Bouldering Grade",
    iconKey: "climbing",
    order: 21,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "First climbs" },
        { score: 2, label: "Beginner", sublabel: "V0-V1" },
        { score: 3, label: "Beginner+", sublabel: "V2-V3" },
        { score: 4, label: "Intermediate", sublabel: "V4-V5" },
        { score: 5, label: "Intermediate+", sublabel: "V6-V7" },
        { score: 6, label: "Advanced", sublabel: "V8-V9" },
        { score: 7, label: "Pro", sublabel: "V10+" },
      ],
      sessionConfig: groupSessionConfig(20),
    },
  },
  {
    id: "mixed-martial-arts",
    name: "Mixed Martial Arts",
    metric: "Belt / Experience",
    iconKey: "fighting",
    order: 22,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "First classes" },
        { score: 2, label: "Beginner", sublabel: "White belt" },
        { score: 3, label: "Beginner+", sublabel: "1-2 stripes" },
        { score: 4, label: "Intermediate", sublabel: "Blue belt" },
        { score: 5, label: "Intermediate+", sublabel: "Purple belt" },
        { score: 6, label: "Advanced", sublabel: "Brown belt" },
        { score: 7, label: "Pro", sublabel: "Black belt / Fighter" },
      ],
      sessionConfig: groupSessionConfig(20),
    },
  },
  {
    id: "cricket",
    name: "Cricket",
    metric: "Playing Level",
    iconKey: "cricket",
    order: 23,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "First games" },
        { score: 2, label: "Beginner", sublabel: "Gully cricket" },
        { score: 3, label: "Beginner+", sublabel: "Weekend games" },
        { score: 4, label: "Intermediate", sublabel: "Local clubs" },
        { score: 5, label: "Intermediate+", sublabel: "Corporate leagues" },
        { score: 6, label: "Advanced", sublabel: "District level" },
        { score: 7, label: "Pro", sublabel: "State / National" },
      ],
      sessionConfig: { ...teamConfig(4, 26), resultSchema: "runs_wickets" },
    },
  },
  {
    id: "golf",
    name: "Golf",
    metric: "Handicap",
    iconKey: "golf",
    order: 24,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "Learning basics" },
        { score: 2, label: "Beginner", sublabel: "Handicap 36+" },
        { score: 3, label: "Beginner+", sublabel: "Handicap 28-36" },
        { score: 4, label: "Intermediate", sublabel: "Handicap 18-28" },
        { score: 5, label: "Intermediate+", sublabel: "Handicap 10-18" },
        { score: 6, label: "Advanced", sublabel: "Handicap 5-10" },
        { score: 7, label: "Pro", sublabel: "Scratch / Plus" },
      ],
      sessionConfig: groupSessionConfig(8),
    },
  },
  {
    id: "cycling",
    name: "Cycling",
    metric: "Avg Speed",
    iconKey: "cycling",
    order: 25,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "<15 km/h" },
        { score: 2, label: "Beginner", sublabel: "15-20 km/h" },
        { score: 3, label: "Beginner+", sublabel: "20-23 km/h" },
        { score: 4, label: "Intermediate", sublabel: "23-26 km/h" },
        { score: 5, label: "Intermediate+", sublabel: "26-30 km/h" },
        { score: 6, label: "Advanced", sublabel: "30-35 km/h" },
        { score: 7, label: "Pro", sublabel: "35+ km/h" },
      ],
      sessionConfig: groupSessionConfig(200),
    },
  },
  {
    id: "walking",
    name: "Walking",
    metric: "Distance",
    iconKey: "walking",
    order: 26,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "<2 km" },
        { score: 2, label: "Beginner", sublabel: "2-4 km" },
        { score: 3, label: "Beginner+", sublabel: "4-6 km" },
        { score: 4, label: "Intermediate", sublabel: "6-8 km" },
        { score: 5, label: "Intermediate+", sublabel: "8-12 km" },
        { score: 6, label: "Advanced", sublabel: "12-18 km" },
        { score: 7, label: "Pro", sublabel: "18+ km" },
      ],
      sessionConfig: groupSessionConfig(200),
    },
  },
  {
    id: "hiit",
    name: "HIIT",
    metric: "Intensity Level",
    iconKey: "hiit",
    order: 27,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "First workouts" },
        { score: 2, label: "Beginner", sublabel: "Light intervals" },
        { score: 3, label: "Beginner+", sublabel: "Regular classes" },
        { score: 4, label: "Intermediate", sublabel: "High intensity" },
        { score: 5, label: "Intermediate+", sublabel: "Advanced intervals" },
        { score: 6, label: "Advanced", sublabel: "Elite conditioning" },
        { score: 7, label: "Pro", sublabel: "Athlete / Coach" },
      ],
      sessionConfig: groupSessionConfig(50),
    },
  },
  {
    id: "hot-yoga",
    name: "Hot Yoga",
    metric: "Practice Level",
    iconKey: "hot-yoga",
    order: 28,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "First hot classes" },
        { score: 2, label: "Beginner", sublabel: "Learning poses in heat" },
        { score: 3, label: "Beginner+", sublabel: "Regular practice" },
        { score: 4, label: "Intermediate", sublabel: "Flowing sequences" },
        { score: 5, label: "Intermediate+", sublabel: "Advanced hot flows" },
        { score: 6, label: "Advanced", sublabel: "Intense practice" },
        { score: 7, label: "Teacher", sublabel: "Certified instructor" },
      ],
      sessionConfig: groupSessionConfig(50),
    },
  },
  {
    id: "nutrition",
    name: "Nutrition",
    metric: "Habit Level",
    iconKey: "nutrition",
    order: 29,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "Tracking meals" },
        { score: 2, label: "Beginner", sublabel: "Calorie awareness" },
        { score: 3, label: "Beginner+", sublabel: "Macro tracking" },
        { score: 4, label: "Intermediate", sublabel: "Balanced nutrition" },
        { score: 5, label: "Intermediate+", sublabel: "Meal prep mastery" },
        { score: 6, label: "Advanced", sublabel: "Optimized performance" },
        { score: 7, label: "Expert", sublabel: "Dietitian / Coach" },
      ],
      sessionConfig: groupSessionConfig(50),
    },
  },
  {
    id: "scuba-diving",
    name: "Scuba Diving",
    metric: "Certification",
    iconKey: "scuba-diving",
    order: 30,
    metadata: {
      levels: [
        { score: 1, label: "Discovery", sublabel: "Try Scuba / DSD" },
        { score: 2, label: "Open Water", sublabel: "OWD Certified" },
        { score: 3, label: "Advanced", sublabel: "AOWD Certified" },
        { score: 4, label: "Rescue", sublabel: "Rescue Diver" },
        { score: 5, label: "Master Diver", sublabel: "Master Scuba Diver" },
        { score: 6, label: "Divemaster", sublabel: "DM / Professional" },
        { score: 7, label: "Instructor", sublabel: "OWSI / Trainer" },
      ],
      sessionConfig: groupSessionConfig(50),
    },
  },
  {
    id: "snorkeling",
    name: "Snorkeling",
    metric: "Experience Level",
    iconKey: "snorkeling",
    order: 31,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "First time in water" },
        { score: 2, label: "Beginner", sublabel: "Shallow reef swims" },
        { score: 3, label: "Beginner+", sublabel: "Comfortable swimmer" },
        { score: 4, label: "Intermediate", sublabel: "Deep reef exploring" },
        { score: 5, label: "Intermediate+", sublabel: "Basic free immersion" },
        { score: 6, label: "Advanced", sublabel: "Skin diving / Breath-hold" },
        { score: 7, label: "Expert", sublabel: "Guide / Safety diver" },
      ],
      sessionConfig: groupSessionConfig(50),
    },
  },
  {
    id: "animal-flow",
    name: "Animal Flow",
    metric: "Practice Level",
    iconKey: "animal-flow",
    order: 32,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "First sessions" },
        { score: 2, label: "Beginner", sublabel: "Basic positions" },
        { score: 3, label: "Beginner+", sublabel: "Regular practice" },
        { score: 4, label: "Intermediate", sublabel: "Fluid transitions" },
        { score: 5, label: "Intermediate+", sublabel: "Advanced movements" },
        { score: 6, label: "Advanced", sublabel: "Complex flows" },
        { score: 7, label: "Certified", sublabel: "Certified instructor" },
      ],
      sessionConfig: groupSessionConfig(50),
    },
  },
  {
    id: "bouldering",
    name: "Bouldering",
    metric: "Bouldering Grade",
    iconKey: "bouldering",
    order: 33,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "First climbs" },
        { score: 2, label: "Beginner", sublabel: "V0-V1" },
        { score: 3, label: "Beginner+", sublabel: "V2-V3" },
        { score: 4, label: "Intermediate", sublabel: "V4-V5" },
        { score: 5, label: "Intermediate+", sublabel: "V6-V7" },
        { score: 6, label: "Advanced", sublabel: "V8-V9" },
        { score: 7, label: "Expert", sublabel: "V10+" },
      ],
      sessionConfig: groupSessionConfig(50),
    },
  },
  {
    id: "stretching",
    name: "Stretching",
    metric: "Flexibility Level",
    iconKey: "stretching",
    order: 34,
    metadata: {
      levels: [
        { score: 1, label: "Initiation", sublabel: "Basic stretches" },
        { score: 2, label: "Beginner", sublabel: "Daily routines" },
        { score: 3, label: "Beginner+", sublabel: "Improved mobility" },
        { score: 4, label: "Intermediate", sublabel: "Deep stretching" },
        { score: 5, label: "Intermediate+", sublabel: "Advanced mobility" },
        { score: 6, label: "Advanced", sublabel: "Splits & backbends" },
        { score: 7, label: "Expert", sublabel: "Movement specialist" },
      ],
      sessionConfig: groupSessionConfig(50),
    },
  },
] as const;

export type ActivitySeedId = (typeof activitySeedData)[number]["id"];

export default activitySeedData;
